// FAQ role model — resolves the viewer's FAQ audience branches from the REAL
// role/permission state (config/roles.js + auth user), never from a user
// choice (TP_FAQ_BRIEF §4, FAQ_00_INDEX resolution matrix).
//
// The additive tier model (FAQ_00_INDEX.md):
//   0      Basis — every logged-in user
//   1a     Agent (solo baseline)
//   2a     Agency member additions
//   3a     Agency admin additions
//   1s     Staff/Coach without club (job seeking, self-application)
//   1c     Club member (staff/coach inside a club)
//   2c     Club admin additions
//   lawyer Lawyer work surfaces
//
// The 8 user types of the matrix and their branch sets:
//   Agent ohne Agentur            0 + 1a
//   Agent in Agentur (Mitglied)   0 + 1a + 2a
//   Agentur-Admin                 0 + 1a + 2a + 3a
//   Staff/Coach ohne Verein       0 + 1s
//   Club-Mitglied/Staff/Coach     0 + 1s + 1c
//   Club-Admin                    0 + 1s + 1c + 2c
//   Lawyer (only)                 0 + lawyer
//   Dual-Rollen                   union of their branches
//
// Membership detection follows the platform's existing single source for the
// same question, utils/inboxRoles.js (hasClubMembership) — club_name/club_id
// on the auth user; agency mirrors it via agency_name/agency_id (the fields
// authSlice.updateFromProfile maintains).

import { ROLES } from "../config/roles";
import { hasClubMembership } from "./inboxRoles";

export const FAQ_BRANCH = {
  BASIS: "0",
  AGENT: "1a",
  AGENCY_MEMBER: "2a",
  AGENCY_ADMIN: "3a",
  STAFF_FREE: "1s",
  CLUB_MEMBER: "1c",
  CLUB_ADMIN: "2c",
  LAWYER: "lawyer",
  // G1 (2026-08-12): mini-branches for the two inbox-centred account types.
  // ONLY the matching role resolves to them — no other type inherits these,
  // and they inherit nothing beyond basis (confidentiality boundary intact).
  PLAYER: "player",
  ORGANIZER: "organizer",
};

// The resolvable user types (+ the degenerate basis-only "member" case for
// any role the matrix does not list). Since G1 (2026-08-12) Player and
// Organizer accounts resolve to their own mini-branches instead of falling
// through to "member".
export const FAQ_USER_TYPES = {
  "agent-solo": {
    branches: [FAQ_BRANCH.BASIS, FAQ_BRANCH.AGENT],
    label: "Agent",
  },
  "agency-member": {
    branches: [FAQ_BRANCH.BASIS, FAQ_BRANCH.AGENT, FAQ_BRANCH.AGENCY_MEMBER],
    label: "Agency member",
  },
  "agency-admin": {
    branches: [
      FAQ_BRANCH.BASIS,
      FAQ_BRANCH.AGENT,
      FAQ_BRANCH.AGENCY_MEMBER,
      FAQ_BRANCH.AGENCY_ADMIN,
    ],
    label: "Agency admin",
  },
  "staff-free": {
    branches: [FAQ_BRANCH.BASIS, FAQ_BRANCH.STAFF_FREE],
    label: "Staff",
  },
  "club-member": {
    branches: [FAQ_BRANCH.BASIS, FAQ_BRANCH.STAFF_FREE, FAQ_BRANCH.CLUB_MEMBER],
    label: "Club member",
  },
  "club-admin": {
    branches: [
      FAQ_BRANCH.BASIS,
      FAQ_BRANCH.STAFF_FREE,
      FAQ_BRANCH.CLUB_MEMBER,
      FAQ_BRANCH.CLUB_ADMIN,
    ],
    label: "Club admin",
  },
  lawyer: {
    branches: [FAQ_BRANCH.BASIS, FAQ_BRANCH.LAWYER],
    label: "Lawyer",
  },
  player: {
    branches: [FAQ_BRANCH.BASIS, FAQ_BRANCH.PLAYER],
    label: "Player",
  },
  organizer: {
    branches: [FAQ_BRANCH.BASIS, FAQ_BRANCH.ORGANIZER],
    label: "Organizer",
  },
  member: {
    branches: [FAQ_BRANCH.BASIS],
    label: "Member",
  },
};

export const hasAgencyMembership = (user) =>
  Boolean(user?.agency_name || user?.agency_id || user?.agency_account_id);

// resolveFaqAudience({ roles, user }) → { typeKeys, branches:Set, label }
// `override` (a FAQ_USER_TYPES key) is the internal dev/test-only escape hatch
// the work order allows — production callers never pass it from user input.
export const resolveFaqAudience = ({ roles = [], user = null } = {}, override = null) => {
  if (override && FAQ_USER_TYPES[override]) {
    const t = FAQ_USER_TYPES[override];
    return { typeKeys: [override], branches: new Set(t.branches), label: t.label };
  }

  const roleList = Array.isArray(roles) ? roles : [];
  const hasAgency = hasAgencyMembership(user);
  const hasClub = hasClubMembership(user);

  const typeKeys = [];
  if (roleList.includes(ROLES.AgencyAdmin)) {
    typeKeys.push("agency-admin");
  } else if (roleList.includes(ROLES.Agent)) {
    typeKeys.push(hasAgency ? "agency-member" : "agent-solo");
  }
  if (roleList.includes(ROLES.ClubAdmin)) {
    typeKeys.push("club-admin");
  } else if (roleList.includes(ROLES.Staff) || roleList.includes(ROLES.Coach)) {
    typeKeys.push(hasClub ? "club-member" : "staff-free");
  }
  if (roleList.includes(ROLES.Lawyer)) typeKeys.push("lawyer");
  if (roleList.includes(ROLES.Player)) typeKeys.push("player");
  if (roleList.includes(ROLES.Organizer)) typeKeys.push("organizer");
  if (typeKeys.length === 0) typeKeys.push("member");

  const branches = new Set();
  typeKeys.forEach((k) => FAQ_USER_TYPES[k].branches.forEach((b) => branches.add(b)));

  // Dual roles: join the distinct labels ("Agent · Lawyer"). Lawyer alongside a
  // working role keeps its label — its branch adds real rows.
  const label = typeKeys.map((k) => FAQ_USER_TYPES[k].label).join(" · ");

  return { typeKeys, branches, label };
};

// A question belongs to the viewer when its audience intersects the viewer's
// branches. Since 2026-08-10 this is a CONFIDENTIALITY test, not a relevance
// one: a question that fails it is not shown, not marked, and not even
// delivered — the page loads only the branch chunks the viewer owns
// (constants/faq/faqBranchData.js). The former "Show all questions" toggle and
// the foreign-role pills are gone with the rule they implemented (Marin:
// "für andere rollen darfst du nicht anzeigen, der Agent und Agency darf sicher
// nicht wissen was alles die Clubs haben!").
//
// It stays exported because it is the assertion the split rests on: every entry
// of a branch module must match every viewer who can load that module
// (constants/faq/faqBranchModules.test.js).
export const questionMatchesAudience = (question, branches) =>
  (question.aud || []).some((b) => branches.has(b));

export default resolveFaqAudience;
